import { Button } from '@/components/ui/button';
import type { ClaimFilters } from '../types/claims.types';
import type { ClaimDateBounds } from '../utils/claim-date-bounds';
import { validateAmounts, validateDateRanges, validateTextFilters } from '../utils/claim-filters';
import { FilterFields } from './FilterFields';
import { FloatingPanel } from './FloatingPanel';

interface ClaimsFilterPanelProps {
  readonly draft: ClaimFilters;
  readonly statuses: readonly string[];
  /** Windows the loaded claims cover, one per date filter (PP-011). */
  readonly dateBounds: ClaimDateBounds;
  onDraftChange: (filters: ClaimFilters) => void;
  onApply: (filters: ClaimFilters) => void;
  onReset: () => void;
  onClose: () => void;
}

/**
 * Collapsible filter panel (US-014). It floats over the page so the grid stays at full width behind it.
 *
 * The draft is owned by the parent: this panel unmounts when it closes, and criteria typed but not yet
 * searched have to survive that (AC-007).
 */
export function ClaimsFilterPanel({
  draft,
  statuses,
  dateBounds,
  onDraftChange,
  onApply,
  onReset,
  onClose,
}: ClaimsFilterPanelProps): React.JSX.Element {
  const dateErrors = validateDateRanges(draft, dateBounds);
  const amountErrors = validateAmounts(draft);
  const textErrors = validateTextFilters(draft);
  const invalid = [
    ...Object.values(dateErrors),
    ...Object.values(amountErrors),
    ...Object.values(textErrors),
  ].some((error) => error !== null);

  const set = <K extends keyof ClaimFilters>(key: K, value: ClaimFilters[K]): void => {
    onDraftChange({ ...draft, [key]: value });
  };

  const toggleStatus = (status: string): void => {
    set(
      'statuses',
      draft.statuses.includes(status)
        ? draft.statuses.filter((candidate) => candidate !== status)
        : [...draft.statuses, status],
    );
  };

  return (
    <FloatingPanel
      title="Filter"
      ariaLabel="Filter claims"
      closeLabel="Close filter panel"
      onClose={onClose}
    >
      <FilterFields
        draft={draft}
        statuses={statuses}
        dateErrors={dateErrors}
        amountErrors={amountErrors}
        textErrors={textErrors}
        set={set}
        toggleStatus={toggleStatus}
      />

      <div className="mt-5 flex gap-2">
        <Button type="button" disabled={invalid} onClick={(): void => { onApply(draft); }} className="flex-1">
          Search
        </Button>
        <Button type="button" variant="secondary" onClick={onReset}>
          Clear
        </Button>
      </div>
    </FloatingPanel>
  );
}
